import React, { useCallback, useRef, useState } from 'react'
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Slider, Stack, Typography } from '@mui/material'
import Cropper, { Area, Point } from 'react-easy-crop'
import { Button } from '../../../components/Button'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { modalCropImageData, modalCropImageVisible } from '../../../redux/reducers/profile/profile.selectors'
import { getCroppedImg, readFile } from './canvaUtils'
import {
  closeModalCropImage,
  getProfessionalProfile,
  postProfessionalProfileImage
} from '../../../redux/reducers/profile/profile.actions'
import { Avatar } from '../../../components/Avatar'
import { showMessage } from '../../../redux/reducers/snackbar/snackbar.actions'
import { useTranslation } from 'react-i18next'
import { storageUserChangeImagePath } from '../../../storage/storageUser'

export const ModalCropImage = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const inputRef = useRef<HTMLInputElement>(null)
  const isVisible = useAppSelector(modalCropImageVisible)
  const data = useAppSelector(modalCropImageData)
  const [imageSrc, setImageSrc] = useState<any>()
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [rotation, setRotation] = useState(0)
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>()
  const [isLoading, setIsLoading] = useState(false)

  const handleFileChange = async (event: any) => {
    setZoom(1)
    setCrop({ x: 0, y: 0 })
    const fileObj = event?.target.files && event.target.files[0]
    if (!fileObj) {
      return
    }
    const file = event.target.files[0]
    const imageDataUrl = await readFile(file)
    const formData = new FormData()
    formData.append('file', file)
    setImageSrc(imageDataUrl)
  }

  const openFile = () => {
    inputRef?.current?.click()
  }

  const onClose = () => {
    setZoom(1)
    setRotation(0)
    setCrop({ x: 0, y: 0 })
    setImageSrc(null)
    dispatch(closeModalCropImage())
  }

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const handleSaveImageProfile = useCallback(async () => {
    if (!imageSrc) {
      onClose()
      return
    }
    try {
      setIsLoading(true)
      const croppedImage: any = await getCroppedImg(imageSrc, croppedAreaPixels, rotation)
      const formData = new FormData()
      formData.append('file', croppedImage)
      const result = await dispatch(postProfessionalProfileImage({ payload: formData })).unwrap()
      storageUserChangeImagePath(result.data.profileImage)
      await dispatch(getProfessionalProfile()).unwrap()
      dispatch(showMessage({ message: t('general.message.successDescription') }))
      onClose()
    } catch (e) {
      dispatch(showMessage({ message: t('general.message.errorDescription'), type: 'error' }))
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }, [imageSrc, croppedAreaPixels, rotation])

  return (
    <Dialog
      fullWidth={true}
      open={isVisible}
      onClose={onClose}
    >
      <DialogTitle>{t('profile.editPhotoTitle')}</DialogTitle>
      <DialogContent>
        <Box p={2} mt={2}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={6}>
              {imageSrc
                ? <Stack style={{ position: 'relative', width: '100%', height: 300 }}>
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    rotation={rotation}
                    zoom={zoom}
                    aspect={1}
                    zoomWithScroll={false}
                    onCropChange={setCrop}
                    onRotationChange={setRotation}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                    cropShape="round"
                  />
                </Stack>
                : <Stack height="100%" justifyContent="center" alignItems="center" width="100%">
                  <Avatar imagePath={data?.url} name={''} size={200} border={3}/>
                </Stack>
              }
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1">{t('profile.zoom')}</Typography>
              <Slider
                value={zoom}
                disabled={!imageSrc}
                min={1}
                max={3}
                step={0.1}
                aria-label={`${t('profile.zoom')}`}
                valueLabelDisplay="auto"
                onChange={(e, zoom) => setZoom(Number(zoom))}
              />

              <Typography variant="subtitle1">{t('profile.rotation')}</Typography>
              <Slider
                value={rotation}
                min={-45}
                max={45}
                disabled={!imageSrc}
                step={1}
                aria-label={`${t('profile.rotation')}`}
                valueLabelDisplay="auto"
                onChange={(e, rotation) => setRotation(Number(rotation))}
              />
            </Grid>
          </Grid>
          <input
            style={{ display: 'none' }}
            ref={inputRef}
            type="file"
            accept="image/png, image/jpeg, image/gif"
            onChange={handleFileChange}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ height: '50px', justifyContent: 'space-between' }}>
        <Button title={t('profile.changePhoto')} variant="outlined" onClick={openFile}/>
        <Stack flexDirection="row">
          <Button title={t('general.btn.save')} isLoading={isLoading} onClick={handleSaveImageProfile}/>
          <Button
            variant="contained"
            sx={{ ml: 1 }}
            color="secondary"
            title={t('general.btn.dismiss')}
            onClick={() => onClose()}
          />
        </Stack>
      </DialogActions>
    </Dialog>
  )
}
